import { forwardRef, useState, type FocusEvent } from 'react';

import { FormField } from '../FormField/FormField';
import { Textarea, type TextareaProps } from '../Textarea/Textarea';

export interface ReasonFieldProps
  extends Omit<TextareaProps, 'minLength' | 'counterLabel' | 'aria-invalid'> {
  /** The visible label, already translated. */
  readonly label: string;
  /** Helper text — what a good reason contains. */
  readonly help?: string;
  /** The minimum the API enforces. D-06 says 5 for every adjustment reason. */
  readonly minLength?: number;
  /** `(current, min) => "3 / 5"`. */
  readonly counterLabel: (current: number, limit: number) => string;
  /**
   * The ONE deficit sentence, already localized and already interpolated:
   * `(missing) => "Le motif doit compter au moins 5 caractères. Il en manque 4."`
   *
   * One string, one place. Five dialogs invented five wordings for this.
   */
  readonly deficitMessage: (missing: number) => string;
  /** A server-side refusal, shown regardless of blur state. */
  readonly error?: string;
  readonly className?: string;
}

/**
 * The mandatory-reason field.
 *
 * Every audited write in this product takes one — file an adjustment, close an
 * open punch, approve a period, reopen a locked period, dismiss an exception,
 * reset a device. They all need: a minimum length, a live counter, an error
 * that appears on blur rather than on the third keystroke, and a field that
 * KEEPS ITS VALUE when the server refuses.
 *
 * Shipping that six times is how six dialogs end up with six behaviours, so it
 * is one component.
 */
export const ReasonField = forwardRef<HTMLTextAreaElement, ReasonFieldProps>(function ReasonField(
  {
    label,
    help,
    minLength = 5,
    counterLabel,
    deficitMessage,
    error,
    required = true,
    className,
    value,
    defaultValue,
    onBlur,
    ...rest
  },
  ref,
) {
  const [touched, setTouched] = useState(false);
  const [uncontrolled, setUncontrolled] = useState(
    typeof defaultValue === 'string' ? defaultValue : '',
  );

  const current = typeof value === 'string' ? value : uncontrolled;
  const missing = Math.max(0, minLength - current.length);

  // The deficit only becomes an error once the user has left the field. The
  // server refusal, if any, always wins.
  const shown = error ?? (touched && missing > 0 ? deficitMessage(missing) : undefined);

  function handleBlur(event: FocusEvent<HTMLTextAreaElement>): void {
    setTouched(true);
    onBlur?.(event);
  }

  return (
    <FormField
      label={label}
      required={required}
      {...(className !== undefined ? { className } : {})}
      {...(help !== undefined ? { help } : {})}
      {...(shown !== undefined ? { error: shown } : {})}
    >
      <Textarea
        {...rest}
        ref={ref}
        minLength={minLength}
        counterLabel={counterLabel}
        required={required}
        onBlur={handleBlur}
        {...(typeof value === 'string'
          ? { value }
          : {
              defaultValue: uncontrolled,
              onChange: (event) => {
                setUncontrolled(event.target.value);
                rest.onChange?.(event);
              },
            })}
      />
    </FormField>
  );
});
